import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { connect } from 'react-redux';
import './MachineHealth.css';
import AlertArchiveModal from './AlertArchiveModal';
import { getMachineHealth, getMachineHealthHistory } from '../../helper/MachineHealthHelper';
import { findCachedIpLog, getIpLogs } from '../../helper/IpLogHelper';
import { ROLE } from '../../enum/role';
import { TIMELINE_CONST } from '../../common/constant/common-constants';
import {
  APP_SERVICE_LABELS,
  BOARD,
  CRITICAL_APPS,
  CRITICAL_SOM_APPS,
  appsNeedRawRuntime,
  extractRawAppsFromIpRecord,
  extractRawHeartbeatFromIpRecord,
  formatMachineSerial,
  formatProcessUptime,
  formatUptimeDisplay,
  getDiskStatus,
  getHealthScoreMeta,
  getNetworkIp,
  hasHealthSnapshot,
  hasSomSnapshot,
  INTERFACE_LABELS,
  mergeAppRuntime,
  mergeHeartbeatBoard,
  normalizeAlertSeverity,
  seriesWithFallback,
} from '../../utils/machinePayloadUtils';

const APP_ICONS = {
  remoteMonitor: '☁',
  ai_engine: '⚙',
  iomt_backend: '⬡',
  psql: '▣',
  fota: '↻',
  connectivity: '⎈',
  backend: '⬢',
  ui: '▣',
  router: '⇄',
};

const METRIC_ICONS = {
  load: '▣',
  memory: '▤',
  cpu: '⚡',
  integrity: '🛡',
  storage: '▦',
  diagnostics: '◉',
  alerts: '🔔',
  apps: '▧',
};

const formatSparkValue = (value, unit = '', formatValue) => {
  if (formatValue) return formatValue(value);
  if (value == null || !Number.isFinite(value)) return '—';
  const formatted = Number.isInteger(value) ? String(value) : value.toFixed(2);
  return unit ? `${formatted}${unit}` : formatted;
};

const Sparkline = ({
  series,
  color = '#3b82f6',
  height = 36,
  filled = false,
  className = '',
  valueLabel = 'Value',
  unit = '',
  formatValue,
}) => {
  const containerRef = useRef(null);
  const [hoverIndex, setHoverIndex] = useState(null);

  const points = useMemo(() => {
    if (!Array.isArray(series) || series.length === 0) return [];
    return series
      .filter((point) => point?.v != null && Number.isFinite(Number(point.v)))
      .map((point) => ({ t: point.t ?? null, v: Number(point.v) }));
  }, [series]);

  const handlePointerMove = useCallback((event) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect?.width || points.length === 0) return;
    const ratio = (event.clientX - rect.left) / rect.width;
    const index = Math.round(ratio * (points.length - 1));
    setHoverIndex(Math.max(0, Math.min(points.length - 1, index)));
  }, [points.length]);

  const handlePointerLeave = useCallback(() => {
    setHoverIndex(null);
  }, []);

  if (!points.length) return null;

  const width = 120;
  const values = points.map((point) => point.v);
  const max = Math.max(...values, 0.001);
  const min = Math.min(...values, 0);
  const range = max - min || 1;

  const coords = points.map((point, index) => {
    const x = (index / (points.length - 1 || 1)) * width;
    const y = height - ((point.v - min) / range) * (height - 4) - 2;
    return { x, y, ...point };
  });

  const polylinePoints = coords.map((point) => `${point.x},${point.y}`).join(' ');
  const areaPoints = `0,${height} ${polylinePoints} ${width},${height}`;

  const activePoint = hoverIndex != null ? coords[hoverIndex] : null;
  const tooltipLeftPct = activePoint ? (activePoint.x / width) * 100 : 0;

  return (
    <div
      ref={containerRef}
      className={`mh-sparkline-wrap ${className}`.trim()}
      onMouseMove={handlePointerMove}
      onMouseLeave={handlePointerLeave}
      role="img"
      aria-label={`${valueLabel} trend chart`}
    >
      <svg className="mh-sparkline" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden="true">
        {filled ? <polygon points={areaPoints} fill={`${color}22`} stroke="none" /> : null}
        <polyline points={polylinePoints} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {activePoint && (
          <>
            <line x1={activePoint.x} y1={0} x2={activePoint.x} y2={height} className="mh-sparkline-crosshair" />
            <circle
              cx={activePoint.x}
              cy={activePoint.y}
              r="3.5"
              className="mh-sparkline-dot"
              fill={color}
              stroke="#fff"
              strokeWidth="1.5"
            />
          </>
        )}
      </svg>
      {activePoint && (
        <div
          className="mh-sparkline-tooltip"
          style={{ left: `${tooltipLeftPct}%`, borderColor: color }}
        >
          <span className="mh-sparkline-tooltip-label">{valueLabel}</span>
          <strong>{formatSparkValue(activePoint.v, unit, formatValue)}</strong>
          <span className="mh-sparkline-tooltip-time">
            {activePoint.t
              ? moment(activePoint.t).format(`${TIMELINE_CONST.DATE_FORMAT} ${TIMELINE_CONST.TIME_FORMAT}`)
              : 'Snapshot'}
          </span>
        </div>
      )}
    </div>
  );
};

const MetricHead = ({ icon, title, badge }) => (
  <div className="mh-metric-head">
    <div className="mh-metric-title">
      <span className="mh-metric-icon" aria-hidden="true">{icon}</span>
      <h2>{title}</h2>
    </div>
    {badge}
  </div>
);

const SummaryCard = ({ tone, icon, label, value, badge, meta, valueClassName = '' }) => (
  <article className="mh-summary-card">
    <div className={`mh-summary-icon mh-summary-icon--${tone}`}>{icon}</div>
    <div className="mh-summary-content">
      <span className="mh-summary-label">{label}</span>
      <div className="mh-summary-value-row">
        <strong className={`mh-summary-value ${valueClassName}`.trim()}>{value}</strong>
        {badge}
      </div>
      {meta && <span className="mh-summary-meta">{meta}</span>}
    </div>
  </article>
);

const formatMemMb = (value) => {
  if (value == null || !Number.isFinite(Number(value))) return null;
  const num = Number(value);
  return Number.isInteger(num) || Math.abs(num % 1) < 0.05 ? String(Math.round(num)) : num.toFixed(1);
};

const formatCount = (value) => {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  return Math.round(Number(value)).toLocaleString();
};

const MemoryDonut = ({
  usedPct,
  freeMb,
  availableMb,
  usedMb,
  swapMb,
  swapTotalMb,
  memoryTrendSeries,
  swapTrendSeries,
  primaryLabel = 'Available Memory',
  warnAvailableBelowMb = null,
}) => {
  const displayAvailableMb = availableMb ?? freeMb;
  const showFree = freeMb != null
    && availableMb != null
    && Number(availableMb) !== Number(freeMb);
  const availableWarn = warnAvailableBelowMb != null
    && displayAvailableMb != null
    && Number(displayAvailableMb) < warnAvailableBelowMb;

  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const safePct = Number.isFinite(usedPct) ? Math.min(100, Math.max(0, usedPct)) : 0;
  const offset = circumference - (safePct / 100) * circumference;
  const displayPct = Number.isFinite(usedPct) ? Number(usedPct).toFixed(1) : '—';

  return (
    <div className="mh-memory-panel">
      <div className="mh-memory-body">
        <div className="mh-donut-wrap">
          <svg viewBox="0 0 140 140" aria-hidden="true">
            <circle cx="70" cy="70" r={radius} className="mh-donut-track" />
            <circle
              cx="70"
              cy="70"
              r={radius}
              className="mh-donut-fill"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
            />
          </svg>
          <div className="mh-donut-center">
            <strong>{displayPct}%</strong>
            <span>Used</span>
          </div>
        </div>
        <div className="mh-memory-stats">
          <div className="mh-memory-stat">
            <span className="mh-memory-dot mh-memory-dot--available" />
            <span>{primaryLabel}</span>
            <strong className={availableWarn ? 'mh-text-warn' : ''}>
              {formatMemMb(displayAvailableMb) ?? '—'} MB
            </strong>
          </div>
          {showFree ? (
            <div className="mh-memory-stat">
              <span className="mh-memory-dot mh-memory-dot--free" />
              <span>Free Memory</span>
              <strong>{formatMemMb(freeMb)} MB</strong>
            </div>
          ) : null}
          <div className="mh-memory-stat">
            <span className="mh-memory-dot mh-memory-dot--used" />
            <span>Used</span>
            <strong>{formatMemMb(usedMb) ?? '—'} MB</strong>
          </div>
          <div className="mh-memory-stat">
            <span className="mh-memory-dot mh-memory-dot--swap" />
            <span>Swap Used</span>
            <strong className={Number(swapMb) > 100 ? 'mh-text-warn' : ''}>{formatMemMb(swapMb) ?? '—'} MB</strong>
          </div>
          <div className="mh-memory-stat">
            <span className="mh-memory-dot mh-memory-dot--swap-total" />
            <span>Swap Total</span>
            <strong>{formatMemMb(swapTotalMb) ?? '—'} MB</strong>
          </div>
        </div>
      </div>
      {memoryTrendSeries?.length > 0 && (
        <Sparkline
          series={memoryTrendSeries}
          color="#22c55e"
          height={32}
          filled
          valueLabel={primaryLabel}
          unit=" MB"
        />
      )}
      {swapTrendSeries?.length > 0 && (
        <div className="mh-memory-swap-trend">
          <span className="mh-memory-swap-trend-label">Swap Used</span>
          <Sparkline
            series={swapTrendSeries}
            color="#8b5cf6"
            height={32}
            filled
            valueLabel="Swap Used"
            unit=" MB"
          />
        </div>
      )}
      <div className="mh-memory-footer-bar">
        <div className="mh-memory-footer-fill" style={{ width: `${safePct}%` }} />
      </div>
    </div>
  );
};

const StorageRow = ({ label, pct }) => {
  const status = getDiskStatus(pct);
  return (
    <div className="mh-storage-row">
      <div className="mh-storage-head">
        <div className="mh-storage-labels">
          <strong>{label}</strong>
          <span className={`mh-storage-pct ${status.className}`}>{pct ?? '—'}%</span>
        </div>
        <span className={`mh-pill ${status.className}`}>{status.label}</span>
      </div>
      <div className="mh-storage-track">
        <div className={`mh-storage-fill ${status.className}`} style={{ width: `${pct ?? 0}%` }} />
      </div>
      <span className="mh-storage-sub">Used {pct ?? '—'}% (-)</span>
    </div>
  );
};

const DiagnosticTile = ({ label, value, unit, meta, series, color, filled = false }) => (
  <div className="mh-diagnostic-tile">
    <span className="mh-diagnostic-label">{label}</span>
    <strong>{value ?? '—'}{unit || ''}</strong>
    {meta ? <span className="mh-diagnostic-meta">{meta}</span> : null}
    <Sparkline series={series} color={color} filled={filled} valueLabel={label} unit={unit} />
  </div>
);

const formatSwapMb = (swapMb) => {
  if (swapMb == null) return '—';
  const value = Number(swapMb);
  if (!Number.isFinite(value)) return '—';
  if (value < 1) return `${Math.round(value * 1024)} KB`;
  return `${value.toFixed(1)} MB`;
};

const formatCpuPct = (cpuPct) => {
  if (cpuPct == null || !Number.isFinite(Number(cpuPct))) return '—';
  return `${Number(cpuPct).toFixed(1)}%`;
};

const formatRssMb = (rssMb) => {
  if (rssMb == null || !Number.isFinite(Number(rssMb))) return '—';
  const value = Number(rssMb);
  return Number.isInteger(value) || Math.abs(value % 1) < 0.05
    ? `${Math.round(value)} MB`
    : `${value.toFixed(1)} MB`;
};

const formatActiveSince = (value) => {
  if (!value) return '—';
  const parsed = moment(value);
  if (!parsed.isValid()) return '—';
  return parsed.format(`${TIMELINE_CONST.DATE_FORMAT} ${TIMELINE_CONST.TIME_FORMAT}`);
};

const AppMetricCell = ({
  value,
  series,
  color,
  valueLabel,
  unit = '',
  formatValue,
  className = '',
}) => (
  <div className={`mh-app-metric ${className}`.trim()}>
    <span>{value}</span>
    <Sparkline
      series={series}
      color={color}
      height={24}
      filled
      className="mh-sparkline--app"
      valueLabel={valueLabel}
      unit={unit}
      formatValue={formatValue}
    />
  </div>
);

const AppRow = ({
  app,
  appCpuPct = {},
  appRssMb = {},
  appUptimeSec = {},
  criticalApps,
}) => {
  const name = app?.name || 'unknown';
  const state = (app?.state || 'unknown').toLowerCase();
  const restarts = app?.restarts;
  const uptimeSec = app?.uptimeSec ?? app?.activeSec ?? app?.active_sec ?? null;
  const activeSince = app?.activeSince
    ?? app?.active_since
    ?? (uptimeSec != null ? moment().subtract(uptimeSec, 'seconds').toISOString() : null);
  const restartWarn = Number(restarts) > 3;
  const swapWarn = Number(app?.swapMb) > 50;
  const appLabel = APP_SERVICE_LABELS[name] || name;

  const cpuSeries = seriesWithFallback(appCpuPct[name], app?.cpuPct);
  const rssSeries = seriesWithFallback(appRssMb[name], app?.rssMb);
  const uptimeSeries = seriesWithFallback(appUptimeSec[name], uptimeSec);
  const uptimeRestarted = (() => {
    if (!uptimeSeries?.length || uptimeSeries.length < 2) return false;
    for (let i = 1; i < uptimeSeries.length; i += 1) {
      const prev = uptimeSeries[i - 1]?.v;
      const curr = uptimeSeries[i]?.v;
      if (prev != null && curr != null && curr < prev * 0.5 && curr < 600) {
        return true;
      }
    }
    return false;
  })();

  return (
    <tr className={[
      state !== 'active' && criticalApps.has(name) ? 'mh-app-row--critical' : '',
      restartWarn || uptimeRestarted ? 'mh-app-row--warn' : '',
    ].filter(Boolean).join(' ')}>
      <td>
        <div className="mh-app-cell">
          <span className="mh-app-icon">{APP_ICONS[name] || '•'}</span>
          <span>{appLabel}</span>
        </div>
      </td>
      <td>
        <span className={`mh-app-state mh-app-state--${state}`}>
          {state === 'active' ? 'Online' : state === 'inactive' ? 'Offline' : (app?.state || '—')}
        </span>
      </td>
      <td className="mh-app-pid">{app?.pid ?? '—'}</td>
      <td>
        <AppMetricCell
          value={formatRssMb(app?.rssMb)}
          series={rssSeries}
          color="#3b82f6"
          valueLabel={`${appLabel} Memory`}
          unit=" MB"
        />
      </td>
      <td className={swapWarn ? 'mh-text-warn' : ''}>{formatSwapMb(app?.swapMb)}</td>
      <td className={uptimeRestarted ? 'mh-text-warn' : ''}>
        {formatProcessUptime(uptimeSec)}
      </td>
      <td className={restartWarn ? 'mh-text-warn' : ''}>{restarts ?? '—'}</td>
      <td className="mh-app-since">{formatActiveSince(activeSince)}</td>
      <td>
        <AppMetricCell
          value={formatCpuPct(app?.cpuPct)}
          series={cpuSeries}
          color="#22c55e"
          valueLabel={`${appLabel} CPU`}
          unit="%"
        />
      </td>
    </tr>
  );
};

const MachineHealth = ({ userRole, activeDevice }) => {
  const { machineUdi: encodedUdi } = useParams();
  const navigate = useNavigate();
  const machineUDI = decodeURIComponent(encodedUdi || '');

  const [board, setBoard] = useState(BOARD.IOMT);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [health, setHealth] = useState(null);
  const [rawAppsByBoard, setRawAppsByBoard] = useState({ iomt: null, som: null });
  const [rawHeartbeatByBoard, setRawHeartbeatByBoard] = useState({ iomt: null, som: null });
  const [history, setHistory] = useState(null);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const isSom = board === BOARD.SOM;

  const fetchHealth = useCallback((isInitial = false) => {
    if (isInitial) {
      setInitialLoading(true);
    } else {
      setRefreshing(true);
    }
    setError('');
    setNotFound(false);

    const applyIpRecord = (record) => {
      if (!record) return;
      setRawHeartbeatByBoard(extractRawHeartbeatFromIpRecord(record));
      setRawAppsByBoard(extractRawAppsFromIpRecord(record));
    };

    getMachineHealth(machineUDI)
      .then((data) => {
        setHealth(data);
        setInitialLoading(false);
        setRefreshing(false);
        const cached = findCachedIpLog(machineUDI);
        if (cached) {
          applyIpRecord(cached);
          return data;
        }
        if (
          !appsNeedRawRuntime(data)
          && (data?.appVersion || data?.app_version)
          && (data?.iomtKernelVersion || data?.iomt_kernel_version)
        ) {
          return data;
        }
        return getIpLogs()
          .then((ipRes) => {
            const list = Array.isArray(ipRes?.data) ? ipRes.data : [];
            applyIpRecord(list.find((item) => item.machineUDI === machineUDI));
            return data;
          })
          .catch(() => data);
      })
      .catch((err) => {
        if (err?.httpStatus === 404) {
          setNotFound(true);
          setHealth(null);
          setError('No health data found for this device.');
          return;
        }
        setError(err?.message || 'Unable to load machine health data.');
        if (isInitial) setHealth(null);
      })
      .finally(() => {
        setInitialLoading(false);
        setRefreshing(false);
      });
  }, [machineUDI]);

  const fetchHistory = useCallback(() => {
    getMachineHealthHistory(machineUDI, { interval: '5m', board })
      .then((data) => {
        setHistory(data);
      })
      .catch(() => {
        // History is optional — snapshot values are used as fallback.
      });
  }, [machineUDI, board]);

  useEffect(() => {
    fetchHealth(true);
  }, [fetchHealth]);

  useEffect(() => {
    setHistory(null);
    fetchHistory();
  }, [fetchHistory]);

  const som = health?.som || null;
  const somStale = Boolean(health?.somStale || som?.stale);

  const boardSource = isSom ? som : health;
  const mergedHeartbeat = mergeHeartbeatBoard(
    boardSource,
    isSom ? rawHeartbeatByBoard.som : rawHeartbeatByBoard.iomt
  );
  const system = mergedHeartbeat.system;
  const appVersion = mergedHeartbeat.appVersion;
  const iomtKernelVersion = mergedHeartbeat.iomtKernelVersion;
  const alerts = Array.isArray(boardSource?.alerts) ? boardSource.alerts : [];
  const apps = mergeAppRuntime(
    boardSource?.apps,
    isSom ? rawAppsByBoard.som : rawAppsByBoard.iomt
  );
  const boardHealthScore = isSom ? som?.healthScore : health?.healthScore;
  const healthMeta = getHealthScoreMeta(boardHealthScore);
  const hasIomtSnapshot = hasHealthSnapshot(health);
  const hasSom = hasSomSnapshot(health);
  const hasBoardSnapshot = isSom
    ? Boolean(som && (boardHealthScore != null || system.memTotalMb != null || system.load1m != null))
    : hasIomtSnapshot;

  const serial = health?.deviceSerial || formatMachineSerial(machineUDI, activeDevice);
  const criticalApps = isSom ? CRITICAL_SOM_APPS : CRITICAL_APPS;
  const activeInterface = health?.network?.activeInterface;
  const historySeries = history?.series || {};
  const appCpuPct = historySeries.appCpuPct || {};
  const appRssMb = historySeries.appRssMb || {};
  const appUptimeSec = historySeries.appUptimeSec || {};

  const loadSeries = useMemo(
    () => seriesWithFallback(historySeries.load1m, system.load1m),
    [historySeries.load1m, system.load1m]
  );

  const iowaitSeries = useMemo(
    () => seriesWithFallback(
      isSom ? historySeries.cpuIowaitPct : historySeries.iowaitPct,
      isSom ? system.cpuIowaitPct ?? system.iowaitPct : system.iowaitPct
    ),
    [historySeries.cpuIowaitPct, historySeries.iowaitPct, system.cpuIowaitPct, system.iowaitPct, isSom]
  );

  const pageFaultSeries = useMemo(() => {
    if (system.pgmajfaultDelta == null) return [];
    return seriesWithFallback(historySeries.pgmajfaultDelta, system.pgmajfaultDelta);
  }, [historySeries.pgmajfaultDelta, system.pgmajfaultDelta]);

  const dStateSeries = useMemo(
    () => seriesWithFallback(null, system.dStateCount),
    [system.dStateCount]
  );

  const cpuSeries = useMemo(
    () => seriesWithFallback(historySeries.cpuUsedPct, system.cpuUsedPct),
    [historySeries.cpuUsedPct, system.cpuUsedPct]
  );

  const memoryTrendSeries = useMemo(
    () => seriesWithFallback(historySeries.memAvailableMb, system.memAvailableMb),
    [historySeries.memAvailableMb, system.memAvailableMb]
  );

  const swapTrendSeries = useMemo(
    () => (isSom ? [] : seriesWithFallback(historySeries.swapUsedMb, system.swapUsedMb)),
    [historySeries.swapUsedMb, system.swapUsedMb, isSom]
  );

  const memoryUsedPct = system.memUsedPct != null
    ? Number(system.memUsedPct)
    : (system.memUsedMb != null && system.memTotalMb
      ? (Number(system.memUsedMb) / Number(system.memTotalMb)) * 100
      : (system.memTotalMb
        ? ((system.memTotalMb - (system.memAvailableMb ?? system.memFreeMb ?? 0)) / system.memTotalMb) * 100
        : 0));

  const uptimeHours = isSom ? som?.uptimeHours : health?.uptimeHours;
  const uptimeSec = isSom ? som?.uptimeSec : health?.uptimeSec;
  const lastUpdated = isSom
    ? (som?.lastUpdated || health?.updatedAt)
    : (health?.lastUpdated || health?.updatedAt);

  if (!(userRole === ROLE.ADMIN || userRole === ROLE.PRODUCTION)) {
    return (
      <div className="mh-unauthorized">
        <h1>Unauthorized Access</h1>
        <p>You do not have the necessary permissions to access this page.</p>
      </div>
    );
  }

  return (
    <div className="mh-page">
      <div className="mh-container">
        <header className="mh-topbar">
          <div className="mh-topbar-left">
            <button type="button" className="mh-back-btn" onClick={() => navigate('/system-health')}>
              ← System Health
            </button>
            <div>
              <h1 className="mh-title">{isSom ? 'SOM Health' : 'IOMT Health'}</h1>
              {(somStale || !hasSom) && (
                <div className="mh-overall-row">
                  {somStale && (
                    <span className="mh-pill mh-pill--stale" title="SOM lastUpdated older than 120s or missing">
                      SOM stale
                    </span>
                  )}
                  {!hasSom && (
                    <span className="mh-pill mh-pill--muted">No SOM data</span>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="mh-topbar-right">
            <div className="mh-board-toggle" role="tablist" aria-label="Board selector">
              <button
                type="button"
                role="tab"
                aria-selected={board === BOARD.IOMT}
                className={`mh-board-btn ${board === BOARD.IOMT ? 'mh-board-btn--active' : ''}`}
                onClick={() => setBoard(BOARD.IOMT)}
              >
                IOMT
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={board === BOARD.SOM}
                className={`mh-board-btn ${board === BOARD.SOM ? 'mh-board-btn--active' : ''}`}
                onClick={() => setBoard(BOARD.SOM)}
                disabled={!hasSom && !initialLoading}
                title={!hasSom ? 'SOM health not reported yet' : 'View SOM board health'}
              >
                SOM
              </button>
            </div>
            <span className="mh-device-chip">{machineUDI}</span>
            <button
              type="button"
              className="mh-refresh-btn"
              onClick={() => {
                fetchHealth(false);
                fetchHistory();
              }}
              disabled={initialLoading || refreshing}
            >
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </header>

        {initialLoading && (
          <div className="mh-state">
            <div className="mh-spinner" />
            <p>Loading machine health…</p>
          </div>
        )}

        {!initialLoading && error && (
          <div className="mh-state mh-state--error">
            <p>{error}</p>
            <Link to="/system-health">Return to System Health</Link>
          </div>
        )}

        {!initialLoading && !notFound && health && (
          <>
            <section className="mh-summary-grid">
              <SummaryCard
                tone="health"
                icon="♥"
                label={isSom ? 'SOM Health' : 'IOMT Health'}
                value={hasBoardSnapshot ? healthMeta.label.toUpperCase() : (isSom ? (somStale ? 'STALE' : 'PENDING') : 'PENDING')}
                meta={
                  isSom && somStale
                    ? 'Last SOM heartbeat is older than 120s'
                    : (!hasBoardSnapshot ? 'Waiting for heartbeat…' : null)
                }
                valueClassName={
                  boardHealthScore === 'ok' ? 'mh-summary-value--health'
                    : boardHealthScore === 'degraded' ? 'mh-summary-value--degraded'
                      : boardHealthScore === 'critical' ? 'mh-summary-value--critical' : ''
                }
              />
              <SummaryCard
                tone="uptime"
                icon="⌛"
                label="Uptime Duration"
                value={formatUptimeDisplay(uptimeHours, uptimeSec)}
              />
              {isSom ? (
                <SummaryCard
                  tone="firmware"
                  icon="⚡"
                  label="CPU Usage"
                  value={system.cpuUsedPct != null ? `${Number(system.cpuUsedPct).toFixed(1)}%` : '—'}
                  meta={system.cpuIdlePct != null ? `Idle ${Number(system.cpuIdlePct).toFixed(1)}%` : 'Primary SOM CPU'}
                />
              ) : (
                <article className="mh-summary-card">
                  <div className="mh-summary-icon mh-summary-icon--firmware">⬡</div>
                  <div className="mh-summary-content mh-summary-versions">
                    {iomtKernelVersion ? (
                      <div className="mh-summary-version-row">
                        <span className="mh-summary-label">IOMT Kernel Version</span>
                        <strong className="mh-summary-value">{iomtKernelVersion}</strong>
                      </div>
                    ) : null}
                    <div className="mh-summary-version-row">
                      <span className="mh-summary-label">App version</span>
                      <strong className="mh-summary-value">{appVersion || '—'}</strong>
                    </div>
                  </div>
                </article>
              )}
            </section>

            {hasBoardSnapshot ? (
              <>
                <section className={`mh-metrics-grid ${isSom ? 'mh-metrics-grid--som' : ''}`}>
                  <article className="mh-metric-card mh-metric-card--load">
                    <MetricHead
                      icon={METRIC_ICONS.load}
                      title="System Load"
                      badge={<span className="mh-chip mh-chip--info">{isSom ? 'SOM' : 'ARM64 ARCH'}</span>}
                    />
                    <div className="mh-load-values">
                      <div><span>1 MIN</span><strong>{system.load1m ?? '—'}</strong></div>
                      <div><span>5 MIN</span><strong>{system.load5m ?? '—'}</strong></div>
                      <div><span>15 MIN</span><strong>{system.load15m ?? '—'}</strong></div>
                    </div>
                    <Sparkline series={loadSeries} color="#3b82f6" height={48} filled valueLabel="Load (1m)" />
                    <div className="mh-load-footer">
                      <span>Running Processes <strong className="mh-text-ok">{system.procsRunning ?? '—'}</strong></span>
                      <span>Blocked Processes <strong className="mh-text-danger">{system.procsBlocked ?? '—'}</strong></span>
                    </div>
                  </article>

                  <article className="mh-metric-card mh-metric-card--memory">
                    <MetricHead
                      icon={METRIC_ICONS.memory}
                      title="Memory Usage"
                      badge={<span className="mh-chip">{system.memTotalMb ?? '—'} MB TOTAL</span>}
                    />
                    <MemoryDonut
                      usedPct={memoryUsedPct}
                      freeMb={system.memFreeMb}
                      availableMb={system.memAvailableMb}
                      usedMb={system.memUsedMb}
                      swapMb={system.swapUsedMb}
                      swapTotalMb={system.swapTotalMb}
                      memoryTrendSeries={memoryTrendSeries}
                      swapTrendSeries={swapTrendSeries}
                      primaryLabel="Available Memory"
                      warnAvailableBelowMb={isSom ? 150 : null}
                    />
                  </article>

                  {isSom ? (
                    <article className="mh-metric-card mh-metric-card--cpu">
                      <MetricHead icon={METRIC_ICONS.cpu} title="CPU Usage" />
                      <div className="mh-cpu-body">
                        <strong className="mh-cpu-value">{formatCpuPct(system.cpuUsedPct)}</strong>
                        <Sparkline series={cpuSeries} color="#f59e0b" height={48} filled valueLabel="CPU Used" unit="%" />
                        <div className="mh-cpu-breakdown">
                          <span>User <strong>{formatCpuPct(system.cpuUserPct)}</strong></span>
                          <span>System <strong>{formatCpuPct(system.cpuSystemPct)}</strong></span>
                          <span>Idle <strong>{formatCpuPct(system.cpuIdlePct)}</strong></span>
                        </div>
                      </div>
                    </article>
                  ) : (
                    <article className="mh-metric-card mh-metric-card--integrity">
                      <MetricHead icon={METRIC_ICONS.integrity} title="System Integrity" />
                      <div className="mh-integrity-body">
                        <div className="mh-integrity-icon">🛡</div>
                        <div>
                          <strong>Security: Verified</strong>
                          <p>{health.firmwareVersion || 'Firmware information unavailable'}</p>
                        </div>
                        <span className="mh-pill mh-pill--ok">Secure</span>
                      </div>
                    </article>
                  )}

                  <article className="mh-metric-card mh-metric-card--storage">
                    <MetricHead icon={METRIC_ICONS.storage} title="Storage Usage" />
                    <StorageRow label="Root Partition" pct={system.rootUsedPct} />
                    {isSom ? (
                      <>
                        <StorageRow label="/usr/local" pct={system.usrLocalUsedPct} />
                        <div className="mh-venti-row">
                          <span>venti.db</span>
                          <strong>
                            {system.ventiDbExists
                              ? `${system.ventiDbKb != null ? `${system.ventiDbKb} KB` : 'present'}`
                              : 'Missing'}
                          </strong>
                          <span className={`mh-pill ${system.ventiDbExists ? 'mh-pill--ok' : 'mh-disk--danger'}`}>
                            {system.ventiDbExists ? 'OK' : 'Missing'}
                          </span>
                        </div>
                      </>
                    ) : (
                      <StorageRow label="Update Partition" pct={system.updateUsedPct} />
                    )}
                  </article>

                  <article className="mh-metric-card mh-metric-card--diagnostics">
                    <MetricHead icon={METRIC_ICONS.diagnostics} title="System Diagnostics" />
                    <div className="mh-diagnostics-row">
                      <DiagnosticTile
                        label={isSom ? 'CPU IOWAIT' : 'IOWAIT'}
                        value={isSom ? (system.cpuIowaitPct ?? system.iowaitPct) : system.iowaitPct}
                        unit="%"
                        color="#3b82f6"
                        filled
                        series={iowaitSeries}
                      />
                      <DiagnosticTile
                        label="D-STATE PROCS"
                        value={system.dStateCount}
                        color="#8b5cf6"
                        filled
                        series={dStateSeries}
                      />
                      <DiagnosticTile
                        label="MAJOR PAGE FAULTS (Δ)"
                        value={system.pgmajfaultDelta}
                        meta={system.pgmajfault != null ? `Total ${formatCount(system.pgmajfault)}` : null}
                        color="#f59e0b"
                        series={pageFaultSeries}
                      />
                    </div>
                  </article>

                  <article className="mh-metric-card mh-metric-card--alerts">
                    <MetricHead
                      icon={METRIC_ICONS.alerts}
                      title={`${isSom ? 'SOM' : 'IOMT'} Alerts`}
                      badge={<span className="mh-alert-count">{alerts.length}</span>}
                    />
                    {alerts.length === 0 ? (
                      <div className="mh-alerts-empty">
                        <div className="mh-alerts-empty-icon">✓</div>
                        <strong>No Active Alerts</strong>
                        <p>All monitored subsystems are operating within nominal parameters.</p>
                        <button type="button" className="mh-view-archive-btn" onClick={() => setArchiveOpen(true)}>
                          View Archive
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="mh-alerts-list">
                          {alerts.map((alert) => (
                            <div
                              key={`${alert.code}-${alert.detail}`}
                              className={`mh-alert-item mh-alert-item--${normalizeAlertSeverity(alert.severity)}`}
                            >
                              <strong>{alert.code}</strong>
                              <span>{alert.detail}</span>
                              <em className="mh-alert-severity">{alert.severity || 'WARN'}</em>
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          className="mh-view-archive-btn mh-view-archive-btn--inline"
                          onClick={() => setArchiveOpen(true)}
                        >
                          View Archive
                        </button>
                      </>
                    )}
                  </article>
                </section>

                {!isSom && activeInterface && (
                  <section className="mh-network-strip">
                    <span className="mh-network-strip-label">Active Network</span>
                    <span className="mh-network-strip-value">
                      {INTERFACE_LABELS[activeInterface] || activeInterface}
                    </span>
                    {getNetworkIp(health, activeInterface, 4) && (
                      <span className="mh-network-strip-ip">{getNetworkIp(health, activeInterface, 4)}</span>
                    )}
                    {health.network?.internetSpeed?.downloadMbps != null && (
                      <span className="mh-network-strip-speed">
                        ↓ {health.network.internetSpeed.downloadMbps} Mbps
                      </span>
                    )}
                  </section>
                )}

                <section className="mh-apps-panel">
                  <div className="mh-apps-head">
                    <div className="mh-metric-title">
                      <span className="mh-metric-icon" aria-hidden="true">{METRIC_ICONS.apps}</span>
                      <h2>{isSom ? 'SOM Application Runtime' : 'Application Runtime'}</h2>
                    </div>
                    <button type="button" className="mh-restart-all" disabled title="Restart requires backend integration">
                      ↻ Restart All
                    </button>
                  </div>
                  <div className="mh-apps-table-wrap">
                    <table className="mh-apps-table">
                      <thead>
                        <tr>
                          <th>Application</th>
                          <th>State</th>
                          <th>PID</th>
                          <th>Memory (RSS)</th>
                          <th>Swap</th>
                          <th>Running</th>
                          <th>Restarts</th>
                          <th>Active since</th>
                          <th>CPU %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {apps.length === 0 ? (
                          <tr>
                            <td colSpan={9} className="mh-apps-empty">No application runtime data reported.</td>
                          </tr>
                        ) : (
                          apps.map((app) => (
                            <AppRow
                              key={app.name}
                              app={app}
                              appCpuPct={appCpuPct}
                              appRssMb={appRssMb}
                              appUptimeSec={appUptimeSec}
                              criticalApps={criticalApps}
                            />
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </section>

                <footer className="mh-status-footer">
                  <span><strong>Board</strong> {isSom ? 'SOM' : 'IOMT'}</span>
                  <span><strong>Device ID</strong> {isSom ? (health.somDeviceId || som?.deviceId || '—') : serial}</span>
                  <span>
                    <strong>Status</strong>{' '}
                    {isSom ? (
                      <em className={somStale ? 'mh-footer-offline' : 'mh-footer-online'}>
                        ● {somStale ? 'Stale' : 'Online'}
                      </em>
                    ) : (
                      <em className={health.isActive === false ? 'mh-footer-offline' : 'mh-footer-online'}>
                        ● {health.isActive === false ? 'Offline' : 'Online'}
                      </em>
                    )}
                  </span>
                  <span><strong>Uptime</strong> {formatUptimeDisplay(uptimeHours, uptimeSec)}</span>
                  {iomtKernelVersion ? (
                    <span><strong>IOMT Kernel</strong> {iomtKernelVersion}</span>
                  ) : null}
                  <span><strong>App</strong> {appVersion || '—'}</span>
                  <span>
                    <strong>Updated</strong>{' '}
                    {lastUpdated ? moment(lastUpdated).format(TIMELINE_CONST.TIME_FORMAT) : '—'}
                  </span>
                </footer>
              </>
            ) : (
              <section className="mh-pending-card">
                <h2>{isSom ? 'SOM Health Pending' : 'Waiting for Heartbeat'}</h2>
                <p>
                  {isSom
                    ? 'This device has not reported SOM health yet, or the last SOM heartbeat is unavailable. Data appears when the device posts somHealth.'
                    : 'This device has not reported system health yet. Data appears after the first heartbeat (~60 seconds after connectivity-manager starts).'}
                </p>
              </section>
            )}
          </>
        )}

        <AlertArchiveModal
          isOpen={archiveOpen}
          onClose={() => setArchiveOpen(false)}
          machineUDI={machineUDI}
          board={board}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  userRole: state.userDetails.userRole,
  activeDevice: state.setactiveDevice.device,
});

export default connect(mapStateToProps)(MachineHealth);
